import i18n from "i18next";
import * as api from "../../api/recipientLists";
import { notify } from "../notification";
import { buildNewRecipientList } from "./index";

export const MAP_FILTER_CRITERIA_HEADER = "MAP_FILTER_CRITERIA_HEADER";

export function mapFilterCriteriaHeader(csvHeader, masterListHeader) {
  return {
    type: MAP_FILTER_CRITERIA_HEADER,
    originalHeader: csvHeader,
    mappedHeader: masterListHeader,
  };
}

// A mapped target is only usable if the Master List still says it's searchable AND not
// explicitly excluded from filtering. Re-checked here (not just at dropdown-render time) because
// the mapping in state can go stale - e.g. it was loaded before the Master List's config changed.
const isMappedHeaderSupported = (recipientHeaders, masterListHeader) => {
  const headerConfig = recipientHeaders[masterListHeader];
  return Boolean(
    headerConfig && headerConfig.searchable && headerConfig.filterable !== false
  );
};

export function validateFilterCriteria() {
  return (dispatch, getState) => {
    const { newRecipientList } = getState();
    const {
      filterHeaderMapping = {},
      headers = [],
      recipientHeaders = {},
      doneStepIdx,
    } = newRecipientList;

    const errors = {};
    const reverseMapping = {};
    const unsupportedHeaders = [];

    headers.forEach((csvHeader) => {
      const masterListHeader = filterHeaderMapping[csvHeader];
      if (!masterListHeader && masterListHeader !== false) {
        errors[csvHeader] = i18n.t(
          "must be mapped to a Master List field or excluded"
        );
      } else if (masterListHeader && reverseMapping[masterListHeader]) {
        errors[csvHeader] = i18n.t(
          "{{masterListHeader}} is already mapped to {{otherCsvHeader}}",
          {
            masterListHeader,
            otherCsvHeader: reverseMapping[masterListHeader],
          }
        );
      } else if (masterListHeader) {
        reverseMapping[masterListHeader] = csvHeader;

        if (!isMappedHeaderSupported(recipientHeaders, masterListHeader)) {
          errors[csvHeader] = i18n.t(
            "{{masterListHeader}} does not support filtering",
            {
              masterListHeader,
            }
          );
          unsupportedHeaders.push(masterListHeader);
        }
      }
    });

    if (!Object.keys(reverseMapping).length) {
      errors.message = i18n.t(
        "You must include at least one column to filter on."
      );
    } else if (unsupportedHeaders.length) {
      errors.message = i18n.t(
        "Fields added for filtering do not support filtration: {{fields}}",
        { fields: unsupportedHeaders.join(", ") }
      );
    }

    if (Object.keys(errors).length) {
      dispatch(buildNewRecipientList({ errors, doneStepIdx: 0 }));
      return false;
    }

    dispatch(
      buildNewRecipientList({
        errors: {},
        doneStepIdx: Math.max(doneStepIdx, 1),
      })
    );
    return true;
  };
}

export function uploadFilterMasterRecipients() {
  return async (dispatch, getState) => {
    const { newRecipientList, storage } = getState();
    const {
      _id: recipientListId,
      masterListId,
      file,
      filterHeaderMapping = {},
      headers = [],
      name,
    } = newRecipientList;
    const { activeDivisionId } = storage;

    try {
      if (!masterListId) {
        throw new Error("Master list ID is required");
      }

      // Only included columns are sent - excluded columns are simply omitted
      const columnMappings = headers
        .filter((csvHeader) => filterHeaderMapping[csvHeader])
        .map((csvHeader) => ({
          csvHeader,
          masterListField: filterHeaderMapping[csvHeader],
          include: true,
        }));

      await api.uploadFilterMasterRecipients({
        recipientListId,
        masterListId,
        activeDivisionId,
        columnMappings,
        file,
      });

      dispatch(
        notify({
          type: "success",
          message: i18n.t("Successfully uploaded {{name}}!", { name }),
        })
      );
    } catch (error) {
      dispatch(buildNewRecipientList({ loading: false }));
      dispatch(
        notify({
          type: "error",
          message:
            error.response?.data || i18n.t("Failed to filter recipients"),
        })
      );
      throw error;
    }
  };
}
